/**
 * One citation: `SOP-001.pdf p.4` (or `Book.xlsx › Sheet1 › rows 3–9`), the
 * quoted passage in a tooltip, and a click that opens the source document in
 * a new tab — at the cited page for a PDF. The content route is authenticated
 * and audited server-side (`document_index.document_read`).
 */

import { Badge, Tooltip } from "@mantine/core";
import { IconFileText } from "@tabler/icons-react";
import { type Citation, citationLabel } from "./citation";
import { useOpenCitation } from "./useOpenCitation";

interface Props {
  citation: Citation;
  /** Show the `[n]` marker before the label. Default `true`. */
  showNumber?: boolean;
}

export function CitationChip({ citation, showNumber = true }: Props) {
  const { open, opening } = useOpenCitation();
  const label = citationLabel(citation);

  return (
    <Tooltip
      label={citation.snippet}
      multiline
      w={360}
      withArrow
      openDelay={200}
      disabled={!citation.snippet}
    >
      <Badge
        component="button"
        type="button"
        variant="light"
        radius="sm"
        leftSection={<IconFileText size={12} />}
        onClick={() => void open(citation)}
        disabled={opening}
        aria-label={`Open source: ${label}`}
        style={{ cursor: "pointer", textTransform: "none" }}
        data-testid="citation-chip"
      >
        {showNumber ? `[${citation.n}] ` : ""}
        {label}
      </Badge>
    </Tooltip>
  );
}
